// ============================================================
// Fassungswächter — merkt, dass der Server inzwischen eine andere Fassung
// ausliefert als die, die gerade im Browser läuft.
//
// WARUM ES DAS BRAUCHT
// Die Anwendung lädt ihre Bildschirme einzeln nach (Karte, Auswertung,
// Verwaltung). Die Namen dieser Dateien enthalten einen Inhaltshash, und nach
// einem Deployment gibt es die alten nicht mehr. Ein Browserfenster, das über
// ein Deployment hinweg offen bleibt, fordert beim nächsten Seitenwechsel eine
// Datei an, die es nicht mehr gibt — und stand vorher vor einer leeren Fläche.
//
// Die Fehlergrenze in App.tsx fängt das AB. Dieser Wächter sorgt dafür, dass es
// gar nicht erst passiert: er merkt den Wechsel, bevor jemand navigiert.
//
// WARUM KEIN ABMELDEN
// Eine veraltete Fassung ist kein Sicherheitsproblem — die Sitzung ist gültig,
// die Daten sind es auch. Abmelden würde offene Entwürfe wegwerfen und den
// Menschen für ein Serverereignis bestrafen. Abgemeldet wird nur, wenn die
// SITZUNG wirklich weg ist; das erledigt die 401-Behandlung in api/http.ts.
// ============================================================

/**
 * Dateiname des Einstiegs-Chunks, mit dem dieses Fenster gestartet ist.
 *
 * Aus dem Dokument gelesen und nicht beim Bauen eingebrannt: das Dokument ist
 * die Quelle der Wahrheit darüber, was der Browser tatsächlich ausführt. Wird
 * einmal beim Laden ausgewertet und dann festgehalten — später hängt der
 * Browser womöglich weitere Skript-Tags an.
 */
const RUNNING_ENTRY: string | null = (() => {
  try {
    const tags = [...document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')];
    const entry = tags.find((t) => /\/assets\/index-[^/]+\.js$/.test(t.src));
    return entry ? new URL(entry.src, location.href).pathname.replace(/^\//, "") : null;
  } catch {
    return null;
  }
})();

/** Wie oft nachgesehen wird, solange das Fenster im Vordergrund ist. */
const POLL_MS = 10 * 60 * 1000;
/** Nicht öfter als das — ein Fenster, das sekündlich den Fokus wechselt, soll nicht hämmern. */
const MIN_ABSTAND_MS = 60 * 1000;

let zuletzt = 0;

/**
 * Einmal nachsehen. `true`, wenn der Server eine andere Fassung ausliefert.
 *
 * Alles, was schiefgehen kann, bedeutet „keine Aussage" und niemals „neue
 * Fassung": ohne Netz, mit einem Zwischenspeicher davor oder bei einer
 * unerwarteten Antwort wäre ein erzwungenes Neuladen schlimmer als das
 * Nichtstun. Deshalb hier ausschliesslich der eindeutige Fall.
 */
export async function neueFassungVerfuegbar(): Promise<boolean> {
  if (!RUNNING_ENTRY) return false; // eigene Fassung unbekannt — dann lieber schweigen
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  const jetzt = Date.now();
  if (jetzt - zuletzt < MIN_ABSTAND_MS) return false;
  zuletzt = jetzt;
  try {
    // no-store: die Datei liegt neben index.html und wird von nginx zwar schon
    // unzwischenspeicherbar ausgeliefert, aber die Prüfung darf sich nicht auf
    // eine Serverkonfiguration verlassen, die jemand später ändert.
    const res = await fetch("/version.json", { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return false;
    const data = (await res.json()) as { entry?: unknown };
    if (typeof data.entry !== "string" || !data.entry) return false;
    return data.entry !== RUNNING_ENTRY;
  } catch {
    return false;
  }
}

/** Für Meldungen und Fehlerberichte: was läuft hier gerade? */
export function laufendeFassung(): string {
  return RUNNING_ENTRY ?? "unbekannt";
}

export { POLL_MS };
